import type { AuthProvider } from '../context/AuthProvider';
import type { I18nState } from '../context/AweniContext';

const VISITS_KEY = 'aweni_push_visits';
const DISMISSED_KEY = 'aweni_push_prompt_dismissed';
const PROMPT_ID = 'aweni-push-soft-prompt';

function toUint8Array(base64Url: string): Uint8Array {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) arr[i] = raw.charCodeAt(i);
  return arr;
}

export class PushService {
  private auth: AuthProvider;
  private i18n: I18nState;
  private initialized = false;

  constructor(auth: AuthProvider, i18n: I18nState) {
    this.auth = auth;
    this.i18n = i18n;
  }

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    const currentVisits = Number(localStorage.getItem(VISITS_KEY) || '0');
    localStorage.setItem(VISITS_KEY, String(currentVisits + 1));
  }

  async handleAuthState(): Promise<void> {
    if (!this.auth.isAuthenticated) {
      await this.unsubscribe();
      this.removePrompt();
      return;
    }

    if (
      !('Notification' in window) ||
      !('serviceWorker' in navigator) ||
      !('PushManager' in window)
    ) {
      return;
    }

    const permission = Notification.permission;
    if (permission === 'granted') {
      await this.ensureSubscribed();
      return;
    }

    const visits = Number(localStorage.getItem(VISITS_KEY) || '0');
    const dismissed = localStorage.getItem(DISMISSED_KEY) === '1';
    if (permission === 'default' && visits >= 2 && !dismissed) {
      this.showSoftPrompt();
    }
  }

  private async ensureSubscribed(): Promise<void> {
    const token = this.auth.getToken();
    if (!token) return;

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
      const viteEnv =
        (import.meta as unknown as { env?: Record<string, string | undefined> }).env || {};
      const vapidPublic = (viteEnv.VITE_VAPID_PUBLIC_KEY || '').trim();
      if (!vapidPublic) return;
      const appServerKey = toUint8Array(vapidPublic) as unknown as BufferSource;

      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appServerKey,
      });
    }

    await fetch('/api/v1/push/subscribe', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(subscription.toJSON()),
    });
  }

  async unsubscribe(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    try {
      const token = this.auth.getToken();
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return;

      const payload = { endpoint: subscription.endpoint };
      if (token) {
        await fetch('/api/v1/push/unsubscribe', {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(payload),
        });
      }

      await subscription.unsubscribe();
    } catch {
      // Unsubscribe is best effort.
    }
  }

  private showSoftPrompt(): void {
    if (document.getElementById(PROMPT_ID)) return;

    const prompt = document.createElement('div');
    prompt.id = PROMPT_ID;
    prompt.style.cssText = [
      'position:fixed',
      'left:12px',
      'right:12px',
      'bottom:124px',
      'z-index:95',
      'background:rgba(15,23,42,0.96)',
      'border:1px solid rgba(148,163,184,0.35)',
      'border-radius:14px',
      'padding:12px 14px',
      'box-shadow:0 10px 30px rgba(0,0,0,0.35)',
      'backdrop-filter:blur(8px)',
      'display:flex',
      'gap:10px',
      'align-items:flex-start',
    ].join(';');

    const content = document.createElement('div');
    content.style.cssText = 'flex:1;min-width:0;';

    const title = document.createElement('p');
    title.style.cssText = 'margin:0 0 6px;font-size:13px;font-weight:700;color:#E2E8F0;';
    title.textContent = this.i18n.t('push_prompt_title');

    const text = document.createElement('p');
    text.style.cssText = 'margin:0;font-size:12px;line-height:1.4;color:#CBD5E1;';
    text.textContent = this.i18n.t('push_prompt_body');

    const actions = document.createElement('div');
    actions.style.cssText = 'display:flex;gap:8px;align-items:center;';

    const allowBtn = document.createElement('button');
    allowBtn.type = 'button';
    allowBtn.textContent = this.i18n.t('push_prompt_allow');
    allowBtn.style.cssText = [
      'height:36px',
      'padding:0 12px',
      'border-radius:10px',
      'border:0',
      'background:#10B981',
      'color:white',
      'font-size:12px',
      'font-weight:700',
      'cursor:pointer',
    ].join(';');

    const laterBtn = document.createElement('button');
    laterBtn.type = 'button';
    laterBtn.textContent = this.i18n.t('push_prompt_later');
    laterBtn.style.cssText = [
      'height:36px',
      'padding:0 10px',
      'border-radius:10px',
      'border:1px solid rgba(148,163,184,0.45)',
      'background:transparent',
      'color:#CBD5E1',
      'font-size:12px',
      'font-weight:600',
      'cursor:pointer',
    ].join(';');

    allowBtn.addEventListener('click', async () => {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          await this.ensureSubscribed();
        }
      } finally {
        localStorage.setItem(DISMISSED_KEY, '1');
        this.removePrompt();
      }
    });

    laterBtn.addEventListener('click', () => {
      localStorage.setItem(DISMISSED_KEY, '1');
      this.removePrompt();
    });

    content.append(title, text);
    actions.append(allowBtn, laterBtn);
    prompt.append(content, actions);

    document.body.appendChild(prompt);
  }

  private removePrompt(): void {
    const prompt = document.getElementById(PROMPT_ID);
    if (prompt) prompt.remove();
  }
}
